import { useState } from 'react'
import { ChevronDown, ChevronUp, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { iconForDashboardNav } from '@/lib/dashboardNavIcons'
import {
  CHOOSE_APPS_FILTERS,
  chooseAppsRows,
  moveTile,
  setTileShown,
  type HomeTile,
} from '@/lib/homeLayouts'
import { cn } from '@/lib/utils'

/**
 * Turns apps on and off for the layout being edited, by column, with search.
 * The up and down buttons move an app on Home without dragging.
 */
export function ChooseAppsDialog({
  open,
  onOpenChange,
  draft,
  allowed,
  onChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  draft: string[]
  /** Every tile the user may see; the list never offers anything else. */
  allowed: HomeTile[]
  onChange: (draft: string[]) => void
}) {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<string>('All')
  const rows = chooseAppsRows(draft, allowed, query, filter)
  const allowedIds = new Set(allowed.map(t => t.id))
  const onHome = draft.filter(id => allowedIds.has(id)).length
  // Up and down swap with the nearest app LISTED, not the nearest on Home: with
  // a search or filter on, the neighbour on Home may be hidden from the list,
  // and a swap with it would look like the button did nothing.
  const shownPositions = rows.filter(r => r.shown).map(r => r.position)
  const neighbour = (position: number, step: -1 | 1) =>
    shownPositions[shownPositions.indexOf(position) + step]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[85vh] max-w-md flex-col gap-3'>
        <DialogHeader>
          <DialogTitle>Choose apps</DialogTitle>
          <DialogDescription>Turn on the apps to show on Home.</DialogDescription>
        </DialogHeader>

        <div className='relative'>
          <Search className='pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder='Search apps'
            aria-label='Search apps'
            className='h-9 pl-8'
          />
        </div>

        <div role='group' aria-label='Filter by column' className='flex rounded-md bg-gray-100 p-0.5'>
          {CHOOSE_APPS_FILTERS.map(f => (
            <button
              key={f}
              type='button'
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
              className={cn(
                'flex-1 rounded px-2 py-1 text-xs font-medium transition-colors',
                filter === f ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {f}
            </button>
          ))}
        </div>

        <p className='text-xs font-medium text-blue-700'>
          {onHome} of {allowed.length} apps on Home
        </p>

        <ul className='-mx-1 min-h-0 flex-1 divide-y divide-gray-100 overflow-y-auto px-1'>
          {rows.length === 0 && <li className='py-6 text-center text-sm text-gray-400'>No apps match.</li>}
          {rows.map(({ tile, shown, position }) => {
            const Icon = iconForDashboardNav(tile.label)
            return (
              <li key={tile.id} className='flex items-center gap-2 py-1.5'>
                <div className='flex w-5 shrink-0 flex-col items-center'>
                  {shown && (
                    <>
                      <button
                        type='button'
                        aria-label={`Move ${tile.label} up`}
                        disabled={neighbour(position, -1) === undefined}
                        onClick={() => onChange(moveTile(draft, position, neighbour(position, -1)))}
                        className='rounded text-gray-400 hover:text-gray-700 disabled:opacity-30'
                      >
                        <ChevronUp className='h-3.5 w-3.5' />
                      </button>
                      <button
                        type='button'
                        aria-label={`Move ${tile.label} down`}
                        disabled={neighbour(position, 1) === undefined}
                        onClick={() => onChange(moveTile(draft, position, neighbour(position, 1)))}
                        className='rounded text-gray-400 hover:text-gray-700 disabled:opacity-30'
                      >
                        <ChevronDown className='h-3.5 w-3.5' />
                      </button>
                    </>
                  )}
                </div>
                <div
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                    shown ? tile.tile : 'bg-gray-300',
                  )}
                >
                  <Icon className='h-4 w-4 text-white' />
                </div>
                <div className='min-w-0 flex-1'>
                  <p className={cn('truncate text-sm font-medium', shown ? 'text-gray-900' : 'text-gray-400')}>
                    {tile.label}
                  </p>
                  <p className='text-[11px] text-gray-400'>{tile.group}</p>
                </div>
                <Switch
                  checked={shown}
                  aria-label={`Show ${tile.label} on Home`}
                  onCheckedChange={on => onChange(setTileShown(draft, tile.id, on))}
                />
              </li>
            )
          })}
        </ul>

        <DialogFooter>
          <Button size='sm' onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
